import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { frontendDb as db } from '../../lib/frontend-db';
import type { Font } from '../../lib/frontend-db';

const FontsPage = () => {
  const [fonts, setFonts] = useState<Font[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loadedFontIds, setLoadedFontIds] = useState<Set<string>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadFonts();
    
    // Cleanup interval and injected CSS on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Clean up all injected preview CSS
      fonts.forEach(font => {
        removeFontCSS(font.id);
      });
    };
  }, []); // Remove fonts dependency to prevent infinite loop

  // Load fonts CSS when fonts change (without infinite loop)
  useEffect(() => {
    if (fonts.length === 0) return;
    
    console.log('📝 FontsPage: Processing fonts for preview:', fonts.map(f => f.name));
    
    // Load only new fonts that haven't been loaded yet
    fonts.forEach(font => {
      if (!loadedFontIds.has(font.id)) {
        console.log(`🆕 Loading font for preview: ${font.name}`);
        loadFontForPreview(font);
        setLoadedFontIds(prev => new Set([...prev, font.id]));
      }
    });
  }, [fonts]); // Remove loadedFontIds dependency to prevent infinite loop

  const loadFonts = async () => {
    try {
      const fontsData = await db.getFonts();
      setFonts(fontsData);
    } catch (error) {
      console.error('Error loading fonts:', error);
      setError('Fehler beim Laden der Schriftarten');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        setError('Bitte wählen Sie eine ZIP-Datei aus');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Bitte wählen Sie eine Datei aus');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const newFont = await db.uploadFont(selectedFile);
      setFonts(prev => [...prev, newFont]);
      setSelectedFile(null);
      setSuccess('Schriftart erfolgreich hochgeladen!');
      
      // Reset file input
      const fileInput = document.getElementById('font-file') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Force reload fonts after a short delay to ensure they're processed
      setTimeout(() => {
        loadFonts();
        // Trigger custom event for DynamicFontLoader (only once)
        console.log('🔔 Triggering font upload event...');
        window.dispatchEvent(new CustomEvent('fontUploaded'));
      }, 1500);
    } catch (error: any) {
      console.error('Error uploading font:', error);
      
      // Try to get more specific error message
      let errorMessage = 'Fehler beim Hochladen der Schriftart';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fontId: string, fontName: string) => {
    if (!window.confirm(`Möchten Sie die Schriftart "${fontName}" wirklich löschen?`)) {
      return;
    }

    try {
      await db.deleteFont(fontId);
      setFonts(prev => prev.filter(f => f.id !== fontId));
      setSuccess('Schriftart erfolgreich gelöscht!');
      
      // Trigger custom event for DynamicFontLoader
      window.dispatchEvent(new CustomEvent('fontUploaded'));
    } catch (error) {
      console.error('Error deleting font:', error);
      setError('Fehler beim Löschen der Schriftart');
    }
  };

  const testFontSystem = async () => {
    try {
      const response = await fetch('/api/fonts/test');
      const result = await response.json();
      console.log('🧪 Font system test result:', result);
      setSuccess('Font-System Test: ' + JSON.stringify(result, null, 2));
    } catch (error) {
      console.error('Font system test error:', error);
      setError('Font-System Test fehlgeschlagen');
    }
  };

  // Font loading functions for preview
  const loadFontForPreview = (font: Font) => {
    const fontFaceCSS = createFontFaceCSS(font);
    injectFontCSS(font.id, fontFaceCSS);
  };

  const createFontFaceCSS = (font: Font): string => {
    const fontFaceRules = font.files.map(file => {
      const format = getFontFormat(file.type);
      const fontPath = `${window.location.origin}${file.path}`;
      console.log(`📁 Font file ${file.name}: ${fontPath} (${format})`);
      return `url("${fontPath}") format("${format}")`;
    }).join(', ');

    console.log(`🎯 Creating font-face CSS for preview ${font.name}:`, fontFaceRules);

    return `
      @font-face {
        font-family: "${font.name}";
        src: ${fontFaceRules};
        font-display: block;
        font-weight: normal;
        font-style: normal;
      }
    `;
  };

  const getFontFormat = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'woff2':
        return 'woff2';
      case 'woff':
        return 'woff';
      case 'ttf':
        return 'truetype';
      case 'otf':
        return 'opentype';
      default:
        return 'truetype';
    }
  };

  const injectFontCSS = (fontId: string, css: string) => {
    // Remove existing style tag for this font
    removeFontCSS(fontId);

    // Create new style tag
    const style = document.createElement('style');
    style.id = `font-preview-${fontId}`;
    style.textContent = css;
    document.head.appendChild(style);
    
    console.log(`💉 Injected preview CSS for font ${fontId}`);
    
    // Test if font is actually loaded
    const fontName = css.match(/font-family:\s*"([^"]+)"/)?.[1];
    if (fontName && 'fonts' in document) {
      setTimeout(() => {
        testFontLoading(fontName);
      }, 200);
    }
  };

  const testFontLoading = (fontName: string) => {
    const testElement = document.createElement('div');
    testElement.style.fontFamily = `"${fontName}", monospace`;
    testElement.style.position = 'absolute';
    testElement.style.visibility = 'hidden';
    testElement.style.fontSize = '20px';
    testElement.textContent = 'Test';
    
    document.body.appendChild(testElement);
    const width1 = testElement.offsetWidth;
    
    testElement.style.fontFamily = 'monospace';
    const width2 = testElement.offsetWidth;
    
    document.body.removeChild(testElement);
    
    const isLoaded = width1 !== width2;
    console.log(`🎯 Font ${fontName} preview loaded: ${isLoaded ? '✅' : '❌'} (width1: ${width1}, width2: ${width2})`);
    
    if (!isLoaded) {
      console.warn(`⚠️ Font ${fontName} failed to load for preview. Check network tab for 404 errors.`);
    }
  };

  const removeFontCSS = (fontId: string) => {
    const existingStyle = document.getElementById(`font-preview-${fontId}`);
    if (existingStyle) {
      existingStyle.remove();
    }
  };

  const getFontPreviewStyle = (font: Font) => {
    // Simply return the font-family since @font-face CSS is injected separately
    return {
      fontFamily: `"${font.name}", sans-serif`
    };
  };

  if (loading) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
                 <h2 className="text-xl font-semibold mb-4 text-gray-900">Schriftarten hochladen</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ZIP-Datei mit Schriftarten
            </label>
            <input
              id="font-file"
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="text-sm text-gray-500 mt-1">
              Laden Sie eine ZIP-Datei hoch, die Schriftarten im Format WOFF2, WOFF, TTF oder OTF enthält.
            </p>
          </div>

          {selectedFile && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                <strong>Ausgewählte Datei:</strong> {selectedFile.name}
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Upload size={20} />
              {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
            </button>
            
            <button
              onClick={testFontSystem}
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              🧪 System testen
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
                 <h2 className="text-xl font-semibold mb-4 text-gray-900">Verfügbare Schriftarten</h2>
        
        {fonts.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            Noch keine Schriftarten hochgeladen.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fonts.map((font) => (
              <div key={font.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg">{font.name}</h3>
                  <button
                    onClick={() => handleDelete(font.id, font.name)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Schriftart löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                
                                                 <div className="space-y-2 mb-3">
                  <div 
                    className="text-2xl p-3 bg-gray-50 rounded border text-gray-900"
                    style={getFontPreviewStyle(font)}
                  >
                    AaBbCcDdEe 123
                  </div>
                  <div 
                    className="text-lg p-2 bg-gray-50 rounded border text-gray-900"
                    style={getFontPreviewStyle(font)}
                  >
                    Liederkranz Bieber
                  </div>
                  <div 
                    className="text-sm p-2 bg-gray-50 rounded border text-gray-900"
                    style={getFontPreviewStyle(font)}
                  >
                    Diese Schriftart wird für alle Texte auf der Website verwendet.
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  <p><strong>Dateien:</strong> {font.files.length}</p>
                  <p><strong>Hochgeladen:</strong> {new Date(font.uploaded_at).toLocaleDateString('de-DE')}</p>
                </div>
                
                <div className="mt-3 flex flex-wrap gap-1">
                  {font.files.map((file, index) => (
                    <span
                      key={index}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                    >
                      {file.type.toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
                 )}
       </div>
       
               {/* Font Testing Section */}
        {fonts.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900">Schriftarten-Test mit Beispieltexten</h2>
            <p className="text-gray-600 mb-6">
              Hier können Sie die hochgeladenen Schriftarten mit verschiedenen Textgrößen und Beispieltexten testen.
            </p>
            <div className="space-y-8">
              {fonts.map((font) => (
                <div key={font.id} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold mb-4 text-gray-900 text-xl border-b pb-2">{font.name}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Überschrift (32px)</label>
                      <div 
                        className="text-3xl p-4 bg-gray-50 rounded border text-gray-900"
                        style={getFontPreviewStyle(font)}
                      >
                        Liederkranz Bieber 1888
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Untertitel (24px)</label>
                      <div 
                        className="text-xl p-3 bg-gray-50 rounded border text-gray-900"
                        style={getFontPreviewStyle(font)}
                      >
                        Tradition und Gemeinschaft seit über 130 Jahren
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Fließtext (16px)</label>
                      <div 
                        className="text-base p-3 bg-gray-50 rounded border text-gray-900 leading-relaxed"
                        style={getFontPreviewStyle(font)}
                      >
                        Herzlich willkommen beim Liederkranz Bieber! Wir sind ein traditionsreicher Gesangverein, 
                        der seit 1888 die Liebe zur Musik pflegt und die Gemeinschaft stärkt. 
                        AaBbCcDdEe 1234567890 ÄÖÜ ß €
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 pt-2 border-t">
                      <strong>Verfügbare Formate:</strong> {font.files.map(f => f.type.toUpperCase()).join(', ')} | 
                      <strong> Hochgeladen:</strong> {new Date(font.uploaded_at).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
     </div>
   );
 };

export default FontsPage; 

